import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import NavigationStepperCst from "@components/NavigationStepperCst";
import SuccessDialogCst from "@components/SuccessDialogCst";
import { LO_SECTION_ANCHOR } from "@pages/Contents/V2/Corporate/UsersGroups/types";
import { PERMISSIONS, usePermissions } from "@utils/permissions";
import { openToast } from "@utils/toastStore";
import { onErrorMessage } from "@utils/utilsApi";
import ProgressModalHeader from "./components/ProgressModalHeader";
import TrackingContentPanel from "./components/TrackingContentPanel";
import { useProgressTree } from "./hooks/useProgressTree";
import { useSaveTracking } from "./hooks/useSaveTracking";
import { useSaveCompletion } from "./hooks/useSaveCompletion";
import { useCompletionManagement } from "./hooks/useCompletionManagement";
import { useTrackingContents } from "./hooks/useTrackingContents";
import { StatusFormKind, StatusFormValues } from "./config/statusFormConfig";
import { fetchRootEnrollment } from "./services/progressTree";
import { getTrackingInitialValues } from "./services/trackingSave";
import {
  EnrollmentProgressTrigger,
  ProgressNode,
  ProgressRootFields,
  XapiActivityState,
} from "./types";
import {
  getCommonStatus,
  getEffectiveSelection,
  toggleNodeSelection,
} from "./utils";
import ChangeStatusView from "./views/ChangeStatusView";
import OverviewView from "./views/OverviewView";
import SelectElementsView from "./views/SelectElementsView";

type ProgressView = "overview" | "select" | "form";

const STEP_INDEX_BY_VIEW: Record<ProgressView, number> = {
  overview: 0,
  select: 0,
  form: 1,
};

interface EnrollmentProgressModalProps {
  trigger: EnrollmentProgressTrigger | null;
  onClose: () => void;
}

const EnrollmentProgressModal: React.FC<EnrollmentProgressModalProps> = ({
  trigger,
  onClose,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("enrollment-progress");
  const { can } = usePermissions();
  const canUpdateStatus = can(PERMISSIONS.enrollment.statusUpdate);
  const canReadTracking = can(PERMISSIONS.enrollment.statusMonitor);
  const canWriteTracking = can(PERMISSIONS.enrollment.statusUpdate);
  const lastTrigger = useRef<EnrollmentProgressTrigger | null>(null);
  const [view, setView] = useState<ProgressView>("overview");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [trackingNode, setTrackingNode] = useState<ProgressNode | null>(null);
  const [trackingIndex, setTrackingIndex] = useState<number | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [isReloadingTree, setIsReloadingTree] = useState(false);
  const [rootOverride, setRootOverride] = useState<ProgressRootFields | null>(
    null,
  );
  const hasSaved = useRef(false);

  if (trigger) lastTrigger.current = trigger;
  const current = trigger ?? lastTrigger.current;

  const {
    tree: loadedTree,
    isLoading,
    hasError,
    mutate,
  } = useProgressTree(trigger);
  const tree = useMemo(
    () =>
      loadedTree && rootOverride
        ? { ...loadedTree, ...rootOverride }
        : loadedTree,
    [loadedTree, rootOverride],
  );
  const { submit: submitTracking, isMutating } = useSaveTracking();
  const { submit: submitCompletion, isMutating: isSavingCompletion } =
    useSaveCompletion();
  const tracking = useTrackingContents(
    current?.corporateId,
    current?.userId,
    trackingNode,
  );

  const effectiveTrackingIndex =
    trackingIndex ?? (tracking.contents.length === 1 ? 0 : null);
  const trackingContent =
    trackingNode && effectiveTrackingIndex !== null
      ? (tracking.contents[effectiveTrackingIndex] ?? null)
      : null;

  const effectiveSelection = tree ? getEffectiveSelection(tree, selected) : [];
  const { management, isLoading: isLoadingManagement } =
    useCompletionManagement(
      current?.corporateId,
      current?.userId,
      view === "form" && !trackingNode && effectiveSelection.length === 1
        ? effectiveSelection[0]
        : null,
    );

  const trackingFormKind: StatusFormKind =
    trackingContent?.kind === "scorm"
      ? trackingContent.scormType === "SCORM_12"
        ? "scorm12"
        : "scorm2004"
      : "xapi";

  const closeTracking = () => {
    setTrackingNode(null);
    setTrackingIndex(null);
  };

  const openTracking = (node: ProgressNode) => {
    setSelected(new Set());
    setTrackingIndex(null);
    setTrackingNode(node);
  };

  const resetFlow = () => {
    setView("overview");
    setSelected(new Set());
    closeTracking();
  };

  const handleExited = () => {
    if (hasSaved.current && lastTrigger.current?.source === "user-detail") {
      document
        .getElementById(LO_SECTION_ANCHOR)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    hasSaved.current = false;
    setRootOverride(null);
    resetFlow();
  };

  const reloadTree = async () => {
    setIsReloadingTree(true);
    try {
      const [, rootFields] = await Promise.all([
        mutate(),
        current
          ? fetchRootEnrollment(current).catch((error: unknown) => {
              console.error(error);
              return null;
            })
          : null,
      ]);
      if (rootFields) setRootOverride(rootFields);
    } finally {
      setIsReloadingTree(false);
    }
  };

  const handleSaved = () => {
    hasSaved.current = true;
    setView("select");
    setSelected(new Set());
    closeTracking();
    setSuccessOpen(true);
    current?.onSuccess?.();
    reloadTree();
  };

  const notifyError = (error: any, fallbackKey: string) => {
    console.error(error);
    const apiMessage = onErrorMessage(error);
    openToast({
      severity: "error",
      message:
        error?.response?.data?.message ||
        (apiMessage !== error?.message && apiMessage) ||
        String(t(fallbackKey)),
    });
  };

  const confirmCompletion = async (values: StatusFormValues) => {
    if (!current || effectiveSelection.length === 0) return;
    try {
      await submitCompletion({
        corporateId: current.corporateId,
        userId: current.userId,
        nodes: effectiveSelection,
        values,
      });
      handleSaved();
    } catch (error: any) {
      notifyError(error, "form.completion-error");
    }
  };

  const confirmTracking = async (
    values: StatusFormValues,
    editedStates?: XapiActivityState[],
  ) => {
    if (!current || !trackingNode || !trackingContent) return;
    try {
      await submitTracking({
        corporateId: current.corporateId,
        userId: current.userId,
        node: trackingNode,
        content: trackingContent,
        values,
        activityStates: editedStates,
      });
      tracking.mutate();
      handleSaved();
    } catch (error: any) {
      notifyError(error, "form.error");
    }
  };

  const toggleNode = (node: ProgressNode) =>
    setSelected((prev) =>
      tree ? toggleNodeSelection(prev, node, tree) : prev,
    );

  const isOverview = view === "overview";

  const closeSuccess = () => setSuccessOpen(false);

  return (
    <>
      <SuccessDialogCst
        open={successOpen}
        title={String(t("success.title"))}
        description={String(t("success.description"))}
        actionLabel={String(t("success.back-to-elements"))}
        onClose={closeSuccess}
      />
      <Dialog
        open={!!trigger}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        TransitionProps={{ onExited: handleExited }}
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.default,
            borderRadius: "16px",
            border: `1px solid ${theme.palette.divider}`,
            height: "680px",
          },
        }}
      >
        <DialogTitle sx={{ padding: "20px 24px 8px" }}>
          <ProgressModalHeader
            title={String(t(isOverview ? "overview.title" : "select.title"))}
            userName={current?.userName}
            objectTitle={current?.objectTitle}
            objectType={current?.objectType ?? "activity"}
            onClose={onClose}
          />
          {!isOverview && (
            <>
              <Typography
                sx={{
                  fontSize: "12px",
                  lineHeight: 1.4,
                  mt: 1,
                  whiteSpace: "pre-line",
                  color: theme.palette.text.primary,
                }}
              >
                {t("select.description")}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <NavigationStepperCst
                  steps={[
                    { key: "select", label: String(t("steps.select")) },
                    { key: "change", label: String(t("steps.change")) },
                  ]}
                  activeStep={STEP_INDEX_BY_VIEW[view]}
                />
              </Box>
            </>
          )}
        </DialogTitle>

        {view === "overview" && (
          <OverviewView
            tree={tree}
            isLoading={isLoading || isReloadingTree}
            hasError={hasError}
            onChangeStatus={
              canUpdateStatus || canReadTracking
                ? () => setView("select")
                : undefined
            }
          />
        )}

        {view === "select" && tree && (
          <SelectElementsView
            tree={tree}
            isLoading={isReloadingTree}
            selected={selected}
            onToggle={toggleNode}
            onCancel={resetFlow}
            onContinue={() => setView("form")}
            canSelect={canUpdateStatus}
            showTrackingAction={canReadTracking}
            onEditTracking={openTracking}
            trackingNode={trackingNode}
            trackingPanel={
              <TrackingContentPanel
                contents={tracking.contents}
                isLoading={tracking.isLoading}
                hasError={tracking.hasError}
                selectedIndex={effectiveTrackingIndex}
                activityTitle={trackingNode?.title}
                onSelect={setTrackingIndex}
                onClose={closeTracking}
              />
            }
            canContinue={trackingNode ? !!trackingContent : selected.size > 0}
          />
        )}

        {view === "form" && tree && trackingContent && (
          <ChangeStatusView
            key={`${trackingNode?.key}-${trackingContent.index}`}
            kind={trackingFormKind}
            selection={[]}
            initialValues={getTrackingInitialValues(trackingContent)}
            statements={trackingContent.statements}
            activityStates={trackingContent.activityStates}
            isSubmitting={isMutating}
            onBack={() => setView("select")}
            onConfirm={canWriteTracking ? confirmTracking : undefined}
          />
        )}

        {view === "form" && tree && !trackingContent && isLoadingManagement && (
          <DialogContent
            sx={{ display: "flex", justifyContent: "center", py: 4 }}
          >
            <CircularProgress />
          </DialogContent>
        )}

        {view === "form" &&
          tree &&
          !trackingContent &&
          !isLoadingManagement && (
            <ChangeStatusView
              key={effectiveSelection.map((node) => node.key).join(",")}
              kind="generic"
              selection={effectiveSelection}
              initialValues={{
                currentStatus:
                  management?.status ?? getCommonStatus(effectiveSelection),
                score:
                  management?.score != null ? String(management.score) : null,
                completionDate:
                  management?.exemptionDateTime ??
                  management?.completionDateTime ??
                  null,
              }}
              isSubmitting={isSavingCompletion}
              onConfirm={canUpdateStatus ? confirmCompletion : undefined}
              onBack={() => setView("select")}
            />
          )}
      </Dialog>
    </>
  );
};

export default EnrollmentProgressModal;
